<template>
  <div
    class="carousel-slider"
    :class="sliderClasses"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousedown="handleMouseDown"
    @touchstart.passive="handleTouchStart"
    @touchend.passive="handleTouchEnd"
  >
    <div class="carousel-slider__wrapper" :style="wrapperStyles">
      <div
        ref="track"
        class="carousel-slider__track"
        :style="trackStyles"
      >
        <div v-if="infiniteLoop && slidesCount > 0"
             v-for="(slide, index) in clonedSlides.before"
             :key="`before-${index}`"
             class="carousel-slide carousel-slide--cloned"
             aria-hidden="true"
             v-html="slide">
        </div>

        <slot></slot>

        <div v-if="infiniteLoop && slidesCount > 0"
             v-for="(slide, index) in clonedSlides.after"
             :key="`after-${index}`"
             class="carousel-slide carousel-slide--cloned"
             aria-hidden="true"
             v-html="slide">
        </div>
      </div>
    </div>

    <slider-controls
      v-if="showSliderControls && slidesCount > 1"
      :class="sliderControlsClass"
      :orientation="orientation"
      :disable-prev="!infiniteLoop && currentIndex === 0"
      :disable-next="!infiniteLoop && currentIndex >= maxIndex"
      @prev="goToPrev"
      @next="goToNext"
    />

    <slider-pagination
      v-if="showSliderPagination && pagesCount > 1"
      :class="sliderPaginationClass"
      :pages-count="pagesCount"
      :current-page="currentPage"
      :orientation="orientation"
      @page-change="goToPage"
    />
  </div>
</template>

<script>
import SliderControls from './slider-controls';
import SliderPagination from './slider-pagination';
import { RandomId } from '@/shared/lib/random';
import { SLIDER_EVENTS } from '@/shared/ui/slider';

const { generateRandomId } = RandomId();

const INDEX_PRECISION = 1e-6;
const DEFAULT_FONT_SIZE = 16;

export default {
  props: {
    id: {
      type: String,
      default: null,
    },
    orientation: {
      type: String,
      default: 'horizontal',
      validator: (value) => ['horizontal'].includes(value),
    },
    slidesToShow: {
      type: Number,
      default: 1,
      validator: (value) => value > 0,
    },
    slidesToScroll: {
      type: Number,
      default: 1,
      validator: (value) => value > 0,
    },
    extraSlideSize: {
      type: [Number, String],
      default: '20%',
      validator: (value) => {
        if (typeof value === 'string') {
          return value.match(/^\d+(\.\d+)?(px|%|rem|em)$/);
        }
        return value >= 0;
      },
    },
    gap: {
      type: [Number, String],
      default: 16,
      validator: (value) => {
        if (typeof value === 'string') {
          return value === 'auto' || value.match(/^\d+(\.\d+)?(px|%|rem|em)$/);
        }
        return value >= 0;
      },
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplayInterval: {
      type: Number,
      default: 3000,
      validator: (value) => value > 0,
    },
    swipeEnabled: {
      type: Boolean,
      default: true,
    },
    animationDuration: {
      type: Number,
      default: 500,
      validator: (value) => value >= 0,
    },
    minSwipeDistance: {
      type: Number,
      default: 50,
    },
    infiniteLoop: {
      type: Boolean,
      default: false,
    },
    sliderControlsClass: {
      type: String,
      default: '',
    },
    showSliderControls: {
      type: Boolean,
      default: true,
    },
    sliderPaginationClass: {
      type: String,
      default: '',
    },
    showSliderPagination: {
      type: Boolean,
      default: true,
    },
    initialSlide: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
  },
  data() {
    return {
      currentIndex: this.initialSlide,
      slidesCount: 0,
      isAnimating: false,
      autoplayTimer: null,
      touchStartPosition: { x: 0, y: 0 },
      isSwiping: false,
      isMouseOver: false,
      resizeObserver: null,
      mutationObserver: null,
      containerWidth: 0,
      rootFontSize: 16,
      parentFontSize: 16,
      clonedSlides: {
        before: [],
        after: []
      },
      originalSlides: [],
      isTransitioning: false,
    };
  },
  components: { SliderControls, SliderPagination },
  computed: {
    getSliderId() {
      return this.id || this.generateRandomId('carousel-');
    },
    sliderClasses() {
      return {
        'carousel-slider--horizontal': this.orientation === 'horizontal',
      };
    },
    isAutoGap() {
      return this.gap === 'auto';
    },
    slideWidth() {
      const viewportWidth = this.containerWidth;
      if (viewportWidth === 0) return '100%';

      const extraPx = this.getValueInPixels(this.extraSlideSize);
      const gapPx = this.getValueInPixels(this.gap);
      const visibleWidth = viewportWidth - (extraPx * 2);
      const totalGaps = this.slidesToShow > 1 ? gapPx * (this.slidesToShow - 1) : 0;
      const slideWidthPx = Math.max(
        (visibleWidth - totalGaps) / this.slidesToShow,
        50
      );

      return `${Math.round(slideWidthPx)}px`;
    },
    wrapperStyles() {
      const extraPx = this.getValueInPixels(this.extraSlideSize);

      const baseStyles = {
        transition: `padding ${this.animationDuration}ms ease-in-out`,
      };

      if (!this.infiniteLoop && this.slidesCount > 0) {
        const isFirstSlide = this.currentIndex === 0;
        const isLastSlide = this.currentIndex >= this.maxIndex;

        return {
          ...baseStyles,
          paddingLeft: isFirstSlide ? '0px' : `${extraPx}px`,
          paddingRight: isLastSlide ? '0px' : `${extraPx}px`,
        };
      }

      return {
        ...baseStyles,
        paddingLeft: `${extraPx}px`,
        paddingRight: `${extraPx}px`,
      };
    },
    trackStyles() {
      if (this.slidesCount === 0) {
        return {
          transform: 'translateX(0)',
          transition: 'none',
          display: 'flex',
          alignItems: 'center',
        };
      }

      const gapPx = this.getValueInPixels(this.gap);
      const slideWidthPx = parseFloat(this.slideWidth);
      const slideOffset = slideWidthPx + gapPx;

      let translateX = 0;

      if (this.infiniteLoop && this.slidesCount > 0) {
        const clonedCount = this.clonedSlides.before.length;
        const totalIndex = this.currentIndex + clonedCount;
        translateX = totalIndex * slideOffset;
      } else {
        const boundedIndex = Math.min(Math.max(this.currentIndex, 0), this.maxIndex);
        translateX = boundedIndex * slideOffset;
      }

      return {
        transform: `translateX(-${translateX}px)`,
        transition: (this.isAnimating && !this.isTransitioning) ? `transform ${this.animationDuration}ms ease-in-out` : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: `${gapPx}px`,
      };
    },
    maxIndex() {
      return Math.max(0, this.slidesCount - this.slidesToShow);
    },
    lastIndex() {
      return Math.max(0, Math.ceil(this.maxIndex - INDEX_PRECISION));
    },
    pagesCount() {
      if (this.slidesCount === 0) return 0;
      if (this.infiniteLoop) return Math.ceil(this.slidesCount / this.slidesToScroll);
      return Math.ceil(this.lastIndex / this.slidesToScroll) + 1;
    },
    currentPage() {
      if (!this.infiniteLoop && this.currentIndex >= this.lastIndex) {
        return Math.max(0, this.pagesCount - 1);
      }
      return Math.floor(this.actualCurrentIndex / this.slidesToScroll);
    },
    actualCurrentIndex() {
      if (!this.infiniteLoop) {
        return this.currentIndex;
      }

      let normalizedIndex = this.currentIndex % this.slidesCount;
      if (normalizedIndex < 0) {
        normalizedIndex += this.slidesCount;
      }
      return normalizedIndex;
    }
  },
  mounted() {
    this.$eventBus.$on(SLIDER_EVENTS.UPDATE, this.handleUpdateSliderEvent);
    this.$eventBus.$on(SLIDER_EVENTS.AUTOPLAY, this.handleAutoplayEvent);
    this.$eventBus.$on(SLIDER_EVENTS.NEXT_SLIDE, this.handleGoToNextSlideEvent);
    this.$eventBus.$on(SLIDER_EVENTS.PREV_SLIDE, this.handleGoToPrevSlideEvent);

    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('mousemove', this.handleMouseMove);

    this.$nextTick(() => {
      this.measure();
      this.initializeSlider();
      this.setupSlides();

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.$el);

      this.mutationObserver = new MutationObserver(this.handleTrackMutation);
      if (this.$refs.track) {
        this.mutationObserver.observe(this.$refs.track, { childList: true });
      }
    });
  },
  beforeDestroy() {
    this.$eventBus.$off(SLIDER_EVENTS.UPDATE, this.handleUpdateSliderEvent);
    this.$eventBus.$off(SLIDER_EVENTS.AUTOPLAY, this.handleAutoplayEvent);
    this.$eventBus.$off(SLIDER_EVENTS.NEXT_SLIDE, this.handleGoToNextSlideEvent);
    this.$eventBus.$off(SLIDER_EVENTS.PREV_SLIDE, this.handleGoToPrevSlideEvent);

    this.stopAutoplay();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
  },
  methods: {
    generateRandomId,
    updateContainerWidth() {
      if (this.$el) {
        this.containerWidth = this.$el.clientWidth;
      }
    },
    readFontSize(el) {
      return parseFloat(getComputedStyle(el).fontSize) || DEFAULT_FONT_SIZE;
    },
    updateFontSizes() {
      this.rootFontSize = this.readFontSize(document.documentElement);
      this.parentFontSize = this.readFontSize(this.$el.parentElement || document.documentElement);
    },
    measure() {
      this.updateFontSizes();
      this.updateContainerWidth();
    },
    getValueInPixels(value) {
      if (typeof value === 'number') return value;
      if (value === 'auto') {
        const autoGap = this.calculateAutoGap();
        return parseFloat(autoGap) || 0;
      }
      if (typeof value !== 'string') return 0;

      const num = parseFloat(value);
      if (isNaN(num)) return 0;

      if (value.includes('%')) {
        return (this.containerWidth * num) / 100;
      } else if (value.includes('rem')) {
        return num * this.rootFontSize;
      } else if (value.includes('em')) {
        return num * this.parentFontSize;
      } else {
        return num;
      }
    },
    getGapValueInPixels() {
      return this.getValueInPixels(this.gap);
    },
    getExtraSlideValueInPixels() {
      return this.getValueInPixels(this.extraSlideSize);
    },
    calculateAutoGap() {
      const extraPx = this.getExtraSlideValueInPixels();
      const availableWidth = this.containerWidth - (extraPx * 2);
      const autoGap = Math.max(availableWidth * 0.05, 8);
      return `${autoGap}px`;
    },
    initializeSlider() {
      this.captureOriginalSlides();
      this.slidesCount = this.originalSlides.length;
      this.jumpTo(this.initialSlide);

      if (this.infiniteLoop && this.slidesCount > 0) {
        this.setupInfiniteLoop();
      }
      this.startAutoplay();
    },
    captureOriginalSlides() {
      if (!this.$refs.track) return;

      this.originalSlides = [];
      const children = Array.from(this.$refs.track.children);

      children.forEach(child => {
        if (!child.classList.contains('carousel-slide--cloned')) {
          const clonedElement = child.cloneNode(true);
          clonedElement.style.width = child.style.width;
          clonedElement.style.minWidth = child.style.minWidth;
          clonedElement.style.maxWidth = child.style.maxWidth;
          this.originalSlides.push(clonedElement.outerHTML);
        }
      });
    },
    setupInfiniteLoop() {
      if (!this.infiniteLoop || this.slidesCount === 0) {
        this.clonedSlides = { before: [], after: [] };
        return;
      }

      const slidesToClone = Math.max(this.slidesToShow + this.slidesToScroll, 2);

      this.clonedSlides.before = [];
      this.clonedSlides.after = [];

      for (let i = 0; i < slidesToClone; i++) {
        const beforeIndex = (this.slidesCount - slidesToClone + i) % this.slidesCount;
        const afterIndex = i % this.slidesCount;

        this.clonedSlides.before.push(this.originalSlides[beforeIndex]);
        this.clonedSlides.after.push(this.originalSlides[afterIndex]);
      }
    },
    updateSlides() {
      this.captureOriginalSlides();
      this.slidesCount = this.originalSlides.length;
      if (this.infiniteLoop && this.slidesCount > 0) {
        this.setupInfiniteLoop();
      }
      this.setupSlides();
      this.startAutoplay();
    },
    setupSlides() {
      if (!this.$refs.track) return;

      const slides = Array.from(this.$refs.track.children);
      const slideWidthValue = this.slideWidth;

      slides.forEach((slide) => {
        slide.style.width = slideWidthValue;
        slide.style.minWidth = slideWidthValue;
        slide.style.maxWidth = slideWidthValue;
        slide.style.flexShrink = '0';
        slide.style.display = 'block';
        slide.style.boxSizing = 'border-box';

        const slideContent = slide.querySelector('.carousel-slide__content');
        if (slideContent) {
          slideContent.style.width = '100%';
          slideContent.style.height = '100%';
        }
      });
    },
    handleResize() {
      this.measure();
      this.setupSlides();
    },
    isRealSlideNode(node) {
      return node.nodeType === 1 && !node.classList.contains('carousel-slide--cloned');
    },
    handleTrackMutation(mutations) {
      const hasRealSlideChange = mutations.some(mutation =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(this.isRealSlideNode)
      );
      if (hasRealSlideChange) {
        this.updateSlides();
      }
    },
    jumpTo(index) {
      const upperBound = this.infiniteLoop ? Math.max(0, this.slidesCount - 1) : this.lastIndex;
      this.currentIndex = Math.max(0, Math.min(upperBound, index));
    },
    goToPage(page) {
      this.goToSlide(page * this.slidesToScroll);
    },
    goToSlide(index) {
      if (this.isAnimating || this.slidesCount <= 1) {
        return;
      }

      if (this.infiniteLoop) {
        this.goToSlideInfinite(index);
      } else {
        this.goToSlideNormal(index);
      }
    },
    goToSlideNormal(index) {
      const newIndex = Math.max(0, Math.min(this.lastIndex, index));

      if (newIndex === this.currentIndex) {
        return;
      }

      this.isAnimating = true;
      this.currentIndex = newIndex;

      setTimeout(() => {
        this.isAnimating = false;
      }, this.animationDuration);
    },
    goToSlideInfinite(targetIndex) {
      this.isAnimating = true;
      this.currentIndex = targetIndex;

      setTimeout(() => {
        this.isAnimating = false;
        this.checkAndResetInfinitePosition();
      }, this.animationDuration);
    },
    checkAndResetInfinitePosition() {
      if (!this.infiniteLoop || this.slidesCount === 0) return;

      let needsReset = false;
      let newIndex = this.currentIndex;

      if (this.currentIndex >= this.slidesCount) {
        newIndex = this.currentIndex % this.slidesCount;
        needsReset = true;
      } else if (this.currentIndex < 0) {
        newIndex = (this.currentIndex % this.slidesCount + this.slidesCount) % this.slidesCount;
        needsReset = true;
      }

      if (needsReset) {
        this.isTransitioning = true;
        this.currentIndex = newIndex;

        requestAnimationFrame(() => {
          this.isTransitioning = false;
        });
      }
    },
    goToPrev() {
      const step = this.slidesToScroll;

      if (this.infiniteLoop) {
        const newIndex = this.currentIndex - step;
        this.goToSlide(newIndex);
      } else {
        const prevIndex = Math.max(0, this.currentIndex - step);
        this.goToSlide(prevIndex);
      }
    },
    goToNext() {
      const step = this.slidesToScroll;

      if (this.infiniteLoop) {
        const newIndex = this.currentIndex + step;
        this.goToSlide(newIndex);
      } else {
        let nextIndex = this.currentIndex + step;

        if (this.autoplay && nextIndex > this.lastIndex) {
          nextIndex = 0;
        } else {
          nextIndex = Math.min(this.lastIndex, nextIndex);
        }

        this.goToSlide(nextIndex);
      }
    },
    startAutoplay() {
      if (
        !this.autoplay ||
        this.slidesCount <= 1 ||
        this.autoplayTimer !== null
      ) {
        return;
      }
      this.autoplayTimer = setInterval(() => {
        if (!this.isMouseOver) {
          this.goToNext();
        }
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    handleMouseEnter() {
      this.isMouseOver = true;
    },
    handleMouseLeave() {
      this.isMouseOver = false;
    },
    handleMouseDown(event) {
      if (event.button !== 0 || !this.swipeEnabled || this.slidesCount <= 1) {
        return;
      }
      this.isSwiping = true;
      this.touchStartPosition = {
        x: event.clientX,
        y: event.clientY,
      };
      this.isAnimating = false;
      event.preventDefault();
    },
    handleMouseMove(event) {
      if (!this.isSwiping) return;
      event.preventDefault();
    },
    handleMouseUp(event) {
      if (!this.isSwiping) return;
      this.isSwiping = false;
      const touchEndPosition = {
        x: event.clientX,
        y: event.clientY,
      };
      const deltaX = touchEndPosition.x - this.touchStartPosition.x;
      const deltaY = touchEndPosition.y - this.touchStartPosition.y;
      const direction = this.getSwipeDirection(deltaX, deltaY);
      this.handleSwipeNavigation(direction);
    },
    handleTouchStart(event) {
      if (!this.swipeEnabled || this.slidesCount <= 1) return;
      this.isSwiping = true;
      this.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      this.isAnimating = false;
    },
    handleTouchEnd(event) {
      if (!this.isSwiping) return;
      this.isSwiping = false;
      const touchEndPosition = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };
      const deltaX = touchEndPosition.x - this.touchStartPosition.x;
      const deltaY = touchEndPosition.y - this.touchStartPosition.y;
      const direction = this.getSwipeDirection(deltaX, deltaY);
      this.handleSwipeNavigation(direction);
    },
    getSwipeDirection(deltaX, deltaY) {
      if (Math.abs(deltaX) > this.minSwipeDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
        return deltaX > 0 ? 'right' : 'left';
      }
      return null;
    },
    handleSwipeNavigation(direction) {
      if (!direction) return;

      if (direction === 'right') {
        this.goToPrev();
      } else if (direction === 'left') {
        this.goToNext();
      }
    },
    handleUpdateSliderEvent({ id, delay = 0 }) {
      if (id === this.getSliderId) {
        setTimeout(() => {
          this.updateSlides();
        }, delay);
      }
    },
    handleAutoplayEvent({ id, autoplay }) {
      if (id === this.getSliderId) {
        autoplay ? this.startAutoplay() : this.stopAutoplay();
      }
    },
    handleGoToNextSlideEvent(id) {
      if (id === this.getSliderId) {
        this.goToNext();
      }
    },
    handleGoToPrevSlideEvent(id) {
      if (id === this.getSliderId) {
        this.goToPrev();
      }
    }
  },
  watch: {
    autoplay: {
      immediate: true,
      handler(enabled) {
        enabled ? this.startAutoplay() : this.stopAutoplay();
      },
    },
    currentIndex(newIndex) {
      this.$emit('slide-change', { id: this.getSliderId, newIndex: this.actualCurrentIndex });
    },
    slidesCount() {
      if (this.infiniteLoop && this.slidesCount > 0) {
        this.setupInfiniteLoop();
      }
      this.startAutoplay();
    },
    slidesToShow() {
      if (!this.infiniteLoop) {
        this.currentIndex = Math.min(this.currentIndex, this.lastIndex);
      }
      this.$nextTick(() => {
        if (this.infiniteLoop) {
          this.setupInfiniteLoop();
        }
        this.setupSlides();
      });
    },
    gap() {
      this.$nextTick(() => {
        this.measure();
        this.setupSlides();
      });
    },
    extraSlideSize() {
      this.$nextTick(() => {
        this.setupSlides();
      });
    },
    infiniteLoop: {
      immediate: true,
      handler(enabled) {
        if (enabled && this.slidesCount > 0) {
          this.$nextTick(() => {
            this.setupInfiniteLoop();
            this.setupSlides();
          });
        } else {
          this.clonedSlides = { before: [], after: [] };
        }
      }
    },
    initialSlide: {
      immediate: true,
      handler(newSlide) {
        if (this.slidesCount > 0) {
          this.jumpTo(newSlide);
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.carousel-slider {
  position: relative;
  overflow: hidden;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;

  &__wrapper {
    position: relative;
    overflow: visible;
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  &--horizontal {
    .carousel-slider__wrapper {
      overflow: visible;
    }

    .carousel-slider__track {
      display: flex;
      will-change: transform;
    }
  }
}

.carousel-slider__wrapper {
  position: relative;
  overflow: visible;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.carousel-slider__track {
  display: flex;
  will-change: transform;
  transform-origin: left center;
}

.carousel-slide {
  box-sizing: border-box;
  flex-shrink: 0;

  &--cloned {
    pointer-events: none;
  }

  .carousel-slide__content {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
  }
}
</style>
